import { useIsFetching, useQuery } from "@tanstack/react-query"

import {
    DepositInfoResponse,
    getDepositInfo,
    waitTimeDepositStatusUpdate,
} from "modules/Booking/common/utils"
import { getOrderPaymentStatus, PaymentStatusData } from "modules/BookingCommon/common/utils"
import { PaymentStatus } from "components/ClientCardAttachment/constants"
import {
    DEPOSIT_STATUSES,
    depositStatusesSuccess,
} from "modules/Booking/common/constants/depositStatuses"
import { Callbacks } from "modules/BookingCommon/common/queries/callbacks"
import { readSearchParams } from "utils/customHooks"
import { queryClient } from "utils/infrastructure/query-client"

export const DEPOSIT_QUERY_KEY = "DEPOSIT_QUERY_KEY"

const DEPOSIT_PAYMENT_STATUS_QUERY_KEY = "DEPOSIT_PAYMENT_STATUS_QUERY_KEY"

function createKey(hash?: string) {
    return [DEPOSIT_QUERY_KEY, hash]
}

function createPaymentStatusKey(orderHash?: string | null | undefined) {
    if (!orderHash) {
        return [DEPOSIT_PAYMENT_STATUS_QUERY_KEY]
    }
    return [DEPOSIT_PAYMENT_STATUS_QUERY_KEY, orderHash]
}

function getDepositHashFromSearchParams() {
    return readSearchParams().get("deposit_hash") ?? undefined
}

function useIsDepositLoading() {
    return useIsFetching({ queryKey: [DEPOSIT_QUERY_KEY] })
}

function useGetDeposit(hash?: string) {
    return useQuery(
        createKey(hash),
        () => {
            if (!hash) return
            return getDepositInfo(hash).then((r) => {
                depositCreatedCallbacks.call(r)
                return r
            })
        },
        { enabled: !!hash, refetchOnWindowFocus: false, staleTime: Infinity, cacheTime: 0 }
    )
}

const depositCreatedCallbacks: Callbacks<DepositInfoResponse> = {
    callbacks: [],
    call: (data) => {
        while (depositCreatedCallbacks.callbacks.length) {
            depositCreatedCallbacks.callbacks.pop()?.(data)
        }
    },
    add: (fn) => {
        depositCreatedCallbacks.callbacks.push(fn)
    },
}

function useGetPaymentStatus(
    hash: string | undefined,
    orderHash: string | null | undefined,
    depositStatus?: DEPOSIT_STATUSES
) {
    return useQuery(
        createPaymentStatusKey(orderHash),
        () => {
            if (!orderHash) return
            return getOrderPaymentStatus(orderHash)
        },
        {
            enabled:
                !!orderHash &&
                !!hash &&
                !depositStatusesSuccess.includes(depositStatus as DEPOSIT_STATUSES),
            refetchOnWindowFocus: false,
            staleTime: Infinity,
            cacheTime: 0,
        }
    )
}

function setPaymentStatusQueryData(orderHash: string, data: PaymentStatusData) {
    queryClient.setQueryData(createPaymentStatusKey(orderHash), data)
}

function getPaymentStatusQueryData(orderHash: string) {
    return queryClient.getQueryData<PaymentStatusData>(createPaymentStatusKey(orderHash))
}

function useGetUpdatedPaymentStatus(
    hash: string | undefined,
    orderHash: string | null | undefined,
    paymentStatus?: PaymentStatus,
    depositStatus?: DEPOSIT_STATUSES
) {
    const shouldRefetch =
        !!hash && !!orderHash && !depositStatusesSuccess.includes(depositStatus as DEPOSIT_STATUSES)

    return useQuery(
        createPaymentStatusKey(orderHash),
        () => {
            if (!hash || !orderHash) return

            if (paymentStatus !== PaymentStatus.WAITING_FOR_CAPTURE) {
                return waitTimeDepositStatusUpdate(orderHash, paymentStatus).then((result) => {
                    setPaymentStatusQueryData(orderHash, result)
                    return result
                })
            }

            return getPaymentStatusQueryData(orderHash)
        },
        {
            enabled: shouldRefetch,
            refetchOnWindowFocus: false,
            staleTime: Infinity,
            cacheTime: 0,
            refetchInterval: shouldRefetch ? 500 : false,
            refetchIntervalInBackground: true,
        }
    )
}

function setDepositDetailsQueryData(depositDetails: DepositInfoResponse) {
    queryClient.setQueryData(createKey(depositDetails.hash), depositDetails)
}

function getDepositDetailsQueryData(depositHash: string) {
    return queryClient.getQueryData<DepositInfoResponse>(createKey(depositHash))
}

function updateDepositDetailsQueryData(
    depositHash: string,
    depositDetails: Partial<DepositInfoResponse>
) {
    const data = getDepositDetailsQueryData(depositHash)
    if (!data) return
    setDepositDetailsQueryData({ ...data, ...depositDetails })
}

export const createdDepositLoader = {
    useGet: useGetDeposit,
    useIsLoading: useIsDepositLoading,
    getHash: getDepositHashFromSearchParams,
    onSuccess: depositCreatedCallbacks.add,
    useGetPaymentStatus: useGetPaymentStatus,
    useGetUpdatedPaymentStatus: useGetUpdatedPaymentStatus,
    setQueryData: setDepositDetailsQueryData,
    getQueryData: getDepositDetailsQueryData,
    updateData: updateDepositDetailsQueryData,
}
